import React, { useEffect, useState } from "react";
import Paper from "@mui/material/Paper";
import Box from "@mui/material/Box";
import { alpha, styled } from "@mui/material/styles";
import { Button, CircularProgress, TablePagination } from "@mui/material";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import FormGroup from "@mui/material/FormGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormControl from "@mui/material/FormControl";
import Checkbox from "@mui/material/Checkbox";
import moment from "moment";
import Select, { SelectChangeEvent } from "@mui/material/Select";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import createInstance from "../../../api/axiosinstance";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import Toast from "../../../utils/toast";
import { toast } from "react-toastify";

const CssTextField = styled(TextField)({
  "& label.Mui-focused": {
    color: "#033538",
  },
  "& label": {
    color: "#033538",
  },
  "& .MuiInput-underline:after": {
    borderBottomColor: "#033538",
  },
  "& .MuiOutlinedInput-root": {
    "& fieldset": {
      borderColor: "#033538",
      borderRadius: 10,
    },
    "&:hover fieldset": {
      borderColor: "#033538",
    },
    "&.Mui-focused fieldset": {
      borderColor: "#033538",
    },
    "& .MuiInputBase-input": {
      color: "#033538",
    },
  },
});

const CssFormControl = styled(FormControl)({
  "& label": {
    color: "#033538",
    "&.Mui-focused": {
      color: "#033538",
    },
  },
  "& .MuiInputBase-root": {
    color: "#033538",
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: "#033538",
      borderRadius: 10,
    },
    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: "#033538",
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: "#033538",
    },
  },
});

const CssSelect = styled(Select)({
  "& .MuiSvgIcon-root": {
    color: "#033538",
    textAlign: "center",
  },
});

const AddOrganization = ({ orgData }) => {
  const aiDishaApi = createInstance("DISHA");
  const [creationDate] = useState(new Date().toISOString());
  const isEditMode = orgData ? true : false;
  const [name, setName] = useState("");
  const [domain, setDomain] = useState("");
  const [emailId, setEmailId] = useState("");
  const [contact, setContact] = useState("");
  const [address, setAddress] = useState("");
  const [sector, setSector] = useState("");
  const [orgId, setOrgId] = useState(
    isEditMode ? orgData.organization_id : null
  );
  const [activeSectors, setActiveSectors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [orgLogo, setOrgLogo] = useState(null); // State for logo file

  const clearForm = () => {
    if (isEditMode) {
      setName(orgData.organization_name);
      setDomain(orgData.organization_domain);
      setEmailId(orgData.email);
      setContact(orgData.contact_number);
      setAddress(orgData.organization_address);
      setSector(orgData.sector);
      setOrgLogo(null); // Reset logo in edit mode
    } else {
      setName("");
      setDomain("");
      setEmailId("");
      setContact("");
      setAddress("");
      setSector("");
      setOrgLogo(null); // Clear logo in add mode
    }
  };

  useEffect(() => {
    getActiveSectors();

    if (orgData) {
      setName(orgData.organization_name);
      setDomain(orgData.organization_domain);
      setEmailId(orgData.email);
      setContact(orgData.contact_number);
      setAddress(orgData.organization_address);
      setSector(orgData.sector);
      setOrgId(orgData.organization_id);
    }
  }, [orgData]);

  const getActiveSectors = async () => {
    try {
      const response = await aiDishaApi.get("/sectors-show-active");
      setActiveSectors(response.data["sectors"]);
      console.log(response.data);
    } catch (error) {
      console.error("Error fetching sectors:", error);
    }
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setOrgLogo(file); // Set the selected file to state
    }
  };

  const handleSubmit = async () => {
    setLoading(true);

    try {
      const formData = new FormData();
      formData.append("organization_name", name);
      formData.append("organization_domain", domain);
      formData.append("email", emailId);
      formData.append("contact_number", contact);
      formData.append("organization_address", address);
      formData.append("status", "Active");
      if (!isEditMode) {
        formData.append("creation_date", creationDate);
      }
      if (orgLogo) {
        formData.append("org_logo", orgLogo); // Append logo file if selected
      }

      if (isEditMode) {
        // Update existing organization
        const response = await aiDishaApi.put(
          `/edit-organization/${orgId}`,
          formData,
          {
            headers: { "Content-Type": "multipart/form-data" },
          }
        );
        console.log("Org updated successfully ", response.data);
        toast.success("Organization updated successfully");
      } else {
        // Create new organization
        const response = await aiDishaApi.post("/add-organization", formData, {
          params: { sector: sector },
          headers: { "Content-Type": "multipart/form-data" },
        });
        clearForm();
        console.log("Org added successfully ", response.data);
        toast.success("Organization added successfully");
      }
    } catch (error) {
      console.log(error)
      console.log(
        "Error with organization operation",
        error.response?.data?.detail || error.message
      );
      toast.error(
        isEditMode ? "Error updating organization" : "Error adding organization"
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <Box
        display="grid"
        gridTemplateColumns="repeat(12, 1fr)"
        sx={{
          marginTop: "auto",
          alignItems: "center",
          borderRadius: "10px",
          padding: "1% 10%",
        }}
      >
        <Box
          gridColumn="span 12"
          sx={{ marginTop: "auto", textAlign: "left", marginBottom: "0%" }}
        >
          <Typography
            sx={{ fontFamily: "poppins", fontSize: "28px", color: "#033538" }}
          >
            {isEditMode ? "Edit Organization" : "Add Organization"}
          </Typography>
        </Box>
        <Box
          gridColumn="span 6"
          sx={{ marginTop: "auto", textAlign: "left", marginBottom: "2%" }}
        >
          <Typography
            sx={{ fontFamily: "poppins", color: "#033538", textAlign: "left" }}
          >
            Organization Name<span style={{ color: "red" }}>*</span>
          </Typography>
          <CssTextField
            label="Enter Organization Name"
            id="custom-css-outlined-input"
            size="small"
            sx={{ width: "95%" }}
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </Box>
        <Box
          gridColumn="span 6"
          sx={{ marginTop: "auto", textAlign: "left", marginBottom: "2%" }}
        >
          <Typography
            sx={{ fontFamily: "poppins", color: "#033538", textAlign: "left" }}
          >
            Organization Domain<span style={{ color: "red" }}>*</span>
          </Typography>
          <CssTextField
            label="Enter Organization Domain"
            id="custom-css-outlined-input"
            size="small"
            sx={{ width: "95%" }}
            value={domain}
            onChange={(e) => setDomain(e.target.value)}
          />
        </Box>
        <Box
          gridColumn="span 6"
          sx={{ marginTop: "auto", textAlign: "left", marginBottom: "2%" }}
        >
          <Typography
            sx={{ fontFamily: "poppins", color: "#033538", textAlign: "left" }}
          >
            Email ID<span style={{ color: "red" }}>*</span>
          </Typography>
          <CssTextField
            label="Enter contact mail id"
            id="custom-css-outlined-input"
            size="small"
            sx={{ width: "95%" }}
            value={emailId}
            onChange={(e) => setEmailId(e.target.value)}
          />
        </Box>
        <Box
          gridColumn="span 6"
          sx={{ marginTop: "auto", textAlign: "left", marginBottom: "2%" }}
        >
          <Typography
            sx={{ fontFamily: "poppins", color: "#033538", textAlign: "left" }}
          >
            Contact Number<span style={{ color: "red" }}>*</span>
          </Typography>
          <CssTextField
            label="Enter contact number"
            id="custom-css-outlined-input"
            size="small"
            sx={{ width: "95%" }}
            value={contact}
            onChange={(e) => setContact(e.target.value)}
          />
        </Box>
        <Box
          gridColumn="span 6"
          sx={{ marginTop: "auto", textAlign: "left", marginBottom: "2%" }}
        >
          <Typography
            sx={{ fontFamily: "poppins", color: "#033538", textAlign: "left" }}
          >
            Organization Address<span style={{ color: "red" }}>*</span>
          </Typography>
          <CssTextField
            label="Enter Organization Address"
            id="custom-css-outlined-input"
            size="small"
            sx={{ width: "98%" }}
            value={address}
            onChange={(e) => setAddress(e.target.value)}
          />
        </Box>
        <Box
          gridColumn="span 6"
          sx={{ marginTop: "auto", textAlign: "left", marginBottom: "2%" }}
        >
          <Typography
            sx={{ fontFamily: "poppins", color: "#033538", textAlign: "left" }}
          >
            Sector<span style={{ color: "red" }}>*</span>
          </Typography>
          <CssFormControl sx={{ width: "95%" }}>
            <InputLabel sx={{ color: "#033538" }}>Select Sector</InputLabel>
            <CssSelect
              label="Select Type of Sector"
              id="demo-simple-select-outlined"
              size="small"
              value={sector}
              onChange={(e) => setSector(e.target.value)}
              sx={{ width: "100%", textAlign: "center" }}
            >
              <MenuItem value="" sx={{ color: "#033538" }}>
                <em>Select Sector</em>
              </MenuItem>
              {activeSectors.map((sector, index) => (
                <MenuItem
                  key={sector.sector_id || `${sector.sector_name}-${index}`} // Use sector_id if available, else fallback to name with index
                  value={sector.sector_name}
                  sx={{ color: "#033538" }}
                >
                  {sector.sector_name}
                </MenuItem>
              ))}
            </CssSelect>
          </CssFormControl>
        </Box>
        <Box
          gridColumn="span 6"
          sx={{ marginTop: "auto", textAlign: "left", marginBottom: "2%" }}
        >
          <Typography
            sx={{ fontFamily: "poppins", color: "#033538", textAlign: "left" }}
          >
            Organization Logo
          </Typography>
          <input
            type="file"
            accept="image/*"
            onChange={handleLogoChange}
            style={{ marginTop: "8px", color: "#033538" }}
          />
          {orgLogo && (
            <Typography
              sx={{ fontFamily: "poppins", color: "#033538", marginTop: "8px" }}
            >
              Selected: {orgLogo.name}
            </Typography>
          )}
        </Box>
        <Box
          gridColumn="span 12"
          sx={{ marginTop: "auto", textAlign: "right", width: "90%" }}
        >
          <Button
            sx={{
              border: "1px solid",
              color: "#033538",
              width: "120px",
              height: "35px",
              borderRadius: "10px",
              marginRight: "2%",
            }}
            onClick={clearForm}
            disabled={loading}
          >
            {isEditMode ? "Reset" : "Clear"}
          </Button>
          <Button
            sx={{
              border: "1px solid",
              color: "white",
              width: "120px",
              height: "35px",
              borderRadius: "10px",
              backgroundColor: "#033538",
              position: "relative",
              "&:hover": {
                backgroundColor: "white",
                color: "#033538",
                border: "1px solid #033538",
              },
            }}
            onClick={handleSubmit}
            disabled={loading}
          >
            {loading ? (
              <CircularProgress
                size={24}
                sx={{
                  color: "#B9B9B9",
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  marginTop: "-12px",
                  marginLeft: "-12px",
                }}
              />
            ) : isEditMode ? (
              "Update"
            ) : (
              "Submit"
            )}
          </Button>
        </Box>
      </Box>
    </div>
  );
};

export default AddOrganization;
